import { useState } from 'react';
import cacIconUrl from '../assets/cac-icon.svg';
import { COLORS, GRADIENTS, FONTS } from '../styles/tokens';

interface CertInfo {
  reader: string;
  subject: string;
  issuer: string;
  serial: string;
  email?: string;
}

interface CacSetupProps {
  username: string;
  onComplete: () => void;
  onSkip: () => void;
  isDark: boolean;
}

/**
 * CAC certificate linking page.
 * Calls /api/cac-detect (Vite dev middleware) which reads the smart card
 * reader directly via macOS system_profiler. No HTTPS or Nginx needed.
 */
export default function CacSetup({ username, onComplete, onSkip, isDark }: CacSetupProps) {
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(false);
  const [certInfo, setCertInfo] = useState<CertInfo | null>(null);
  const [error, setError] = useState('');

  const colors = isDark ? COLORS.dark : COLORS.light;

  const cardShadow = isDark
    ? '4px 4px 12px 0px #0A0A0A, -4px -4px 12px 0px #363636'
    : '6px 6px 20px 0px #DBDBDB, -6px -6px 20px 0px #FFFFFF';

  async function handleDetectCac() {
    setDetecting(true);
    setError('');

    try {
      const response = await fetch('/api/cac-detect');
      const data = await response.json();

      if (data.detected) {
        setCertInfo({
          reader: data.reader ?? 'Unknown',
          subject: data.subject ?? 'Unknown',
          issuer: data.issuer ?? 'Unknown',
          serial: data.serial ?? 'Unknown',
          email: data.email,
        });
        setDetected(true);
      } else {
        setError(
          'No CAC detected. Make sure your card reader is connected and your CAC is fully inserted.',
        );
      }
    } catch {
      setError('Could not reach the CAC detection service. Make sure the Hub dev server is running.');
    } finally {
      setDetecting(false);
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100%',
        fontFamily: FONTS.body,
      }}
    >
      <div
        style={{
          width: 420,
          padding: 32,
          borderRadius: 16,
          backgroundColor: colors.card,
          boxShadow: cardShadow,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
        }}
      >
        {/* Header */}
        <h1
          style={{
            fontFamily: FONTS.heading,
            fontSize: 14,
            letterSpacing: '0.15em',
            color: colors.textSecondary,
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          Link Your CAC
        </h1>

        {/* CAC Icon */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDark ? '#292929' : '#EEEEEE',
            boxShadow: cardShadow,
          }}
        >
          <img
            src={cacIconUrl}
            alt="CAC Card"
            style={{
              width: 40,
              height: 40,
              filter: isDark ? 'none' : 'invert(1)',
            }}
          />
        </div>

        {/* Status indicator */}
        {detected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4ADE80' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke="#4ADE80" strokeWidth="1.5" />
              <path d="M5 8l2 2 4-4" stroke="#4ADE80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 500 }}>CAC detected and linked</span>
          </div>
        ) : detecting ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLORS.accent.blue }}>
            <div
              style={{
                width: 16,
                height: 16,
                border: '2px solid',
                borderColor: COLORS.accent.blue,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontSize: 14 }}>Detecting CAC...</span>
          </div>
        ) : null}

        {/* Description */}
        <p
          style={{
            fontSize: 13,
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
            maxWidth: 340,
            margin: 0,
          }}
        >
          {detected
            ? `Your CAC has been successfully linked to your account (${username}). You can now use your CAC for future logins.`
            : 'Insert your Common Access Card (CAC) into the reader and click "DETECT CAC" to link it with your Vertex account.'}
        </p>

        {/* Certificate info (shown after detection) */}
        {detected && certInfo && (
          <div
            style={{
              width: '100%',
              borderRadius: 8,
              padding: 16,
              backgroundColor: isDark ? '#1f1f23' : '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.15em', color: colors.textSecondary, margin: '0 0 2px' }}>
                READER
              </p>
              <p style={{ fontSize: 13, color: colors.text, margin: 0 }}>
                {certInfo.reader}
              </p>
            </div>
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.15em', color: colors.textSecondary, margin: '0 0 2px' }}>
                SUBJECT
              </p>
              <p style={{ fontSize: 13, color: colors.text, margin: 0, wordBreak: 'break-all' }}>
                {certInfo.subject}
              </p>
            </div>
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.15em', color: colors.textSecondary, margin: '0 0 2px' }}>
                ISSUER
              </p>
              <p style={{ fontSize: 13, color: colors.text, margin: 0, wordBreak: 'break-all' }}>
                {certInfo.issuer}
              </p>
            </div>
            {certInfo.email && (
              <div>
                <p style={{ fontSize: 10, letterSpacing: '0.15em', color: colors.textSecondary, margin: '0 0 2px' }}>
                  EMAIL
                </p>
                <p style={{ fontSize: 13, color: colors.text, margin: 0 }}>
                  {certInfo.email}
                </p>
              </div>
            )}
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.15em', color: colors.textSecondary, margin: '0 0 2px' }}>
                SERIAL
              </p>
              <p style={{ fontSize: 12, color: colors.text, margin: 0, fontFamily: 'monospace' }}>
                {certInfo.serial}
              </p>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p style={{ fontSize: 12, color: '#EF4444', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>{error}</p>
        )}

        {/* Action buttons */}
        {detected ? (
          <button
            onClick={onComplete}
            style={{
              width: '100%',
              height: 50,
              border: 'none',
              borderRadius: 8,
              background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
              color: '#FFFFFF',
              fontFamily: FONTS.heading,
              fontSize: 14,
              letterSpacing: '0.15em',
              cursor: 'pointer',
            }}
          >
            CONTINUE TO HUB
          </button>
        ) : (
          <>
            <button
              onClick={handleDetectCac}
              disabled={detecting}
              style={{
                width: '100%',
                height: 50,
                border: 'none',
                borderRadius: 8,
                background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
                color: '#FFFFFF',
                fontFamily: FONTS.heading,
                fontSize: 14,
                letterSpacing: '0.15em',
                cursor: detecting ? 'not-allowed' : 'pointer',
                opacity: detecting ? 0.6 : 1,
              }}
            >
              {detecting ? 'DETECTING...' : 'DETECT CAC'}
            </button>

            <button
              onClick={onSkip}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textSecondary,
                fontFamily: FONTS.body,
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 4,
              }}
            >
              Skip for now
            </button>
          </>
        )}
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
